import { FC, useState, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import PageHeader from '@/components/ui/PageHeader/PageHeader.tsx';
import { ArrowDownIcon } from '@heroicons/react/20/solid';
import { XMarkIcon, BookOpenIcon, DocumentTextIcon, ChevronRightIcon } from '@heroicons/react/24/outline';
import SelectInput from '@/components/functional/SelectInput/SelectInput.tsx';
import LoadingButton from '@/components/functional/LoadingButton/LoadingButton';
import useCreatePracticeSet from '@/api/useCreatePracticeSet';
import useCreateMixedPracticeSet from '@/api/useCreateMixedPracticeSet';
import { useAuthContext } from '@/context/AuthContext.tsx';
import Routes from '@/router/Routes';
import DailyLimitModal from '@/components/ui/DailyLimitModal/DailyLimitModal';
import { Database } from '@/_constants/database.types';
import useQuestionCategories from '@/api/useQuestionCategories';

type QuestionCategory = Database['public']['Enums']['question_types'];
type PaperSelection = 'Paper 1' | 'Paper 2';
type Step = 'subject' | 'paper' | 'question';

// Selection identifier - includes variant for SectionB
type CategorySelection = string; // e.g., 'Q1', 'Q2', 'SectionB_Image', 'SectionB_Narrative'

interface CategoryOption {
  id: CategorySelection; // Unique identifier for UI selection
  category: QuestionCategory; // Actual category to send to API
  categoryCode: string; // Paper-specific category code (e.g., 'LANG-P1-Q1', 'LANG-P2-Q1')
  hasImages?: boolean; // Filter for Section B questions
  label: string;
  description: string;
  textIndicator?: string; // For Paper 2: "Text 1", "Text 2", or "Both Texts"
}

// Helper function to format labels with orange marks
const formatLabel = (label: string, textIndicator?: string) => {
  const parts = label.split(/(\[\d+\s+marks?\])/);
  return (
    <>
      {parts.map((part, index) => {
        if (part.match(/\[\d+\s+marks?\]/)) {
          return (
            <span key={index} className="text-primary">
              {part}
            </span>
          );
        }
        return <span key={index}>{part}</span>;
      })}
      {textIndicator && (
        <span className="ml-2 text-xs bg-gray-200 text-gray-600 px-2 py-0.5 rounded-full">{textIndicator}</span>
      )}
    </>
  );
};

const PracticeArea: FC = () => {
  const navigate = useNavigate();
  const { session } = useAuthContext();
  const [step, setStep] = useState<Step>('subject');
  const [selectedSubject, setSelectedSubject] = useState<string>('');
  const [selectedPaper, setSelectedPaper] = useState<PaperSelection>('Paper 1');
  const [selectedId, setSelectedId] = useState<CategorySelection | ''>('');
  const [setSize, setSetSize] = useState<number>(1);
  const [showInfoBox, setShowInfoBox] = useState(true);
  const [limitError, setLimitError] = useState<string | null>(null);

  const createPracticeSetMutation = useCreatePracticeSet();
  const createMixedPracticeSetMutation = useCreateMixedPracticeSet();

  // Fetch categories from database
  const { data: dbCategories = [] } = useQuestionCategories();

  // Derive available subjects from DB categories
  const availableSubjects = useMemo(() => {
    const subjects = new Set(dbCategories.map((c) => c.subject).filter(Boolean));
    return Array.from(subjects);
  }, [dbCategories]);

  // Derive available papers for the selected subject
  const papersForSubject = useMemo(() => {
    const papers = new Set(
      dbCategories.filter((c) => c.subject === selectedSubject).map((c) => c.paper_number).filter(Boolean)
    );
    return Array.from(papers) as PaperSelection[];
  }, [dbCategories, selectedSubject]);

  const handleSelectSubject = (subject: string) => {
    setSelectedSubject(subject);
    setStep('paper');
    setSelectedId('');
    setSetSize(1);
  };

  const handleSelectPaper = (paper: PaperSelection) => {
    setSelectedPaper(paper);
    setStep('question');
    setSelectedId('');
    setSetSize(1);
  };

  const handleBack = () => {
    if (step === 'question') {
      setStep('paper');
      setSelectedId('');
      setSetSize(1);
    } else if (step === 'paper') {
      setStep('subject');
    } else {
      navigate(Routes.DASHBOARD);
    }
  };

  // Build category options for Paper 1
  const paper1Options: CategoryOption[] = useMemo(() => {
    const paperCategories = dbCategories.filter(
      (c) => c.subject === 'English Language' && c.paper_number === 'Paper 1'
    );

    const options: CategoryOption[] = [];

    // Paper 1 specific labels and marks (hardcoded for exact display)
    const paper1Config: Record<string, { label: string; marks: string; description: string }> = {
      Q1: {
        label: 'Q1 – Identify & Retrieve',
        marks: '[1 mark]',
        description: 'Indentify & retrieve explicit information',
      },
      Q2: {
        label: 'Q2 – Retrieval & Interpretation',
        marks: '[2 marks]',
        description: 'Find two details from the text that match what the question is asking about',
      },
      Q3: {
        label: 'Q3 – Language & Structure',
        marks: '[6 marks]',
        description: 'Analyse how language and structure create effects',
      },
      Q4: {
        label: 'Q4 – Sustained Evaluation',
        marks: '[15 marks]',
        description: 'Evaluate the text and justify your viewpoint with evidence',
      },
    };

    // Add Q1-Q4 in order
    ['Q1', 'Q2', 'Q3', 'Q4'].forEach((catCode) => {
      const cat = paperCategories.find((c) => c.category === catCode);
      if (cat) {
        const config = paper1Config[catCode];
        options.push({
          id: cat.category,
          category: cat.category as QuestionCategory,
          categoryCode: cat.category_code, // e.g., 'LANG-P1-Q1'
          label: `${config.label} ${config.marks}`,
          description: config.description,
        });
      }
    });

    // Add Section B options
    const sectionBCat = paperCategories.find((c) => c.category === 'SectionB');
    if (sectionBCat) {
      options.push({
        id: 'SectionB_Image',
        category: 'SectionB',
        categoryCode: sectionBCat.category_code, // e.g., 'LANG-P1-Q5'
        hasImages: true,
        label: 'Section B – Image [40 marks]',
        description: 'Write a creative description with accurate English',
      });
      options.push({
        id: 'SectionB_Narrative',
        category: 'SectionB',
        categoryCode: sectionBCat.category_code, // e.g., 'LANG-P1-Q5'
        hasImages: false,
        label: 'Section B – Narrative [40 marks]',
        description: 'Write a creative narrative with accurate English',
      });
    }

    return options;
  }, [dbCategories]);

  // Build category options for Paper 2
  const paper2Options: CategoryOption[] = useMemo(() => {
    const paperCategories = dbCategories.filter(
      (c) => c.subject === 'English Language' && c.paper_number === 'Paper 2'
    );

    const options: CategoryOption[] = [];

    // Paper 2 specific labels and marks (hardcoded for exact display)
    const paper2Config: Record<string, { label: string; marks: string; description: string; textIndicator: string }> = {
      Q1: {
        label: 'Q1 – Identify & Retrieve',
        marks: '[2 marks]',
        description: 'Identify and interpret explicit & implicit information',
        textIndicator: 'Text 1',
      },
      Q2: {
        label: 'Q2 – Identify & Retrieve',
        marks: '[2 marks]',
        description: 'Identify and interpret explicit and implicit information',
        textIndicator: 'Text 1',
      },
      Q3: {
        label: 'Q3 – Language, Structure & Effects',
        marks: '[15 marks]',
        description: 'Analyse how the writer uses language and structure to interest and engage the reader',
        textIndicator: 'Text 1',
      },
      Q4: {
        label: 'Q4 – Identify & Retrieve',
        marks: '[1 mark]',
        description: 'Identify and interpret explicit and implicit information and ideas',
        textIndicator: 'Text 2',
      },
      Q5: {
        label: 'Q5 – Identify & Retrieve',
        marks: '[1 mark]',
        description: 'Identify and interpret explicit and implicit information and ideas',
        textIndicator: 'Text 2',
      },
      Q6: {
        label: 'Q6 – Sustained Evaluation',
        marks: '[15 marks]',
        description: 'Evaluate how successfully the writer has achieved the attempt to create a specific effect',
        textIndicator: 'Text 2',
      },
      Q7A: {
        label: 'Q7a – Find Similarities Between Two Texts',
        marks: '[6 marks]',
        description: 'Identify key ideas in each text, decide what is similar, and briefly explain these similarities',
        textIndicator: 'Both Texts',
      },
      Q7B: {
        label: 'Q7b – Compare Ideas & Perspectives',
        marks: '[14 marks]',
        description:
          "Identify each writer's ideas and viewpoint, explain how they are presented through language and structure, and compare them",
        textIndicator: 'Both Texts',
      },
    };

    // Add Q1-Q7B in order
    ['Q1', 'Q2', 'Q3', 'Q4', 'Q5', 'Q6', 'Q7A', 'Q7B'].forEach((catCode) => {
      const cat = paperCategories.find((c) => c.category === catCode);
      if (cat) {
        const config = paper2Config[catCode];
        options.push({
          id: cat.category,
          category: cat.category as QuestionCategory,
          categoryCode: cat.category_code, // e.g., 'LANG-P2-Q1'
          label: `${config.label} ${config.marks}`,
          description: config.description,
          textIndicator: config.textIndicator,
        });
      }
    });

    // Add Section B as two question choices (like Paper 1), both draw from LANG-P2-B
    const sectionBCat = paperCategories.find((c) => c.category === 'SectionB');
    if (sectionBCat) {
      options.push({
        id: 'SectionB_Q8',
        category: 'SectionB',
        categoryCode: sectionBCat.category_code, // LANG-P2-B
        hasImages: false,
        label: 'Section B – Question 8 [40 marks]',
        description: 'Write a non-fiction piece for a specific audience and purpose',
      });
      options.push({
        id: 'SectionB_Q9',
        category: 'SectionB',
        categoryCode: sectionBCat.category_code, // LANG-P2-B
        hasImages: false,
        label: 'Section B – Question 9 [40 marks]',
        description: 'Write a non-fiction piece for a specific audience and purpose',
      });
    }

    return options;
  }, [dbCategories]);

  // Get current options based on selected paper
  const categoryOptions = selectedPaper === 'Paper 1' ? paper1Options : paper2Options;

  // Get the selected option details
  const selectedOption = categoryOptions.find((opt) => opt.id === selectedId);
  const isSectionB =
    selectedId === 'SectionB_Image' || selectedId === 'SectionB_Narrative' || selectedId === 'SectionB' ||
    selectedId === 'SectionB_Q8' || selectedId === 'SectionB_Q9';


  const handleCategorySelect = (id: CategorySelection | '') => {
    setSelectedId(id);
    // Auto-set to 1 question for Section B
    if (id === 'SectionB_Image' || id === 'SectionB_Narrative' || id === 'SectionB') {
      setSetSize(1);
    }
  };

  const handleStartSet = async () => {
    if (!session?.user?.id || !selectedOption) return;

    setLimitError(null); // Clear previous errors

    try {
      const result = await createPracticeSetMutation.mutateAsync({
        authId: session.user.id,
        questionCategory: selectedOption.category, // needed for practice_attempts metadata column
        categoryCode: selectedOption.categoryCode, // used for question filtering
        setSize,
        hasImages: selectedOption.hasImages,
      });
      console.log(result);

      // Navigate to practice set page
      navigate(`/practice-sets/${result.practice_set_id}`);
    } catch (error: unknown) {
      console.error('Failed to create practice set:', error);

      // Extract error message
      const errorMessage = error instanceof Error ? error.message : 'Failed to create practice set. Please try again.';

      setLimitError(errorMessage);
    }
  };

  const handleMockPastPaper = async () => {
    if (!session?.user?.id) return;

    setLimitError(null); // Clear previous errors

    try {
      // Build categories with category_codes from the current paper options
      const currentOptions = selectedPaper === 'Paper 1' ? paper1Options : paper2Options;

      const categories = currentOptions.map((opt) => ({
        category_code: opt.categoryCode,
        question_count: 1,
        has_images: opt.hasImages,
      }));

      const result = await createMixedPracticeSetMutation.mutateAsync({
        authId: session.user.id,
        categories,
        totalSetSize: categories.length,
        questionCategory: 'MockedPastPaper',
      });

      // Navigate to practice set page
      navigate(`/practice-sets/${result.practice_set_id}`);
    } catch (error: unknown) {
      console.error('Failed to create mock past paper:', error);

      // Extract error message
      const errorMessage =
        error instanceof Error ? error.message : 'Failed to create mock past paper. Please try again.';

      setLimitError(errorMessage);
    }
  };

  const subjectConfig: Record<string, { label: string; subtitle: string; icon: React.ReactNode }> = {
    'English Language': {
      label: 'English Language',
      subtitle: '1EN0 - Edexcel GCSE',
      icon: <DocumentTextIcon className="w-6 h-6 text-primary" />,
    },
    'English Literature': {
      label: 'English Literature',
      subtitle: '1ET0 - Edexcel GCSE',
      icon: <BookOpenIcon className="w-6 h-6 text-purple-600" />,
    },
  };

  const paperConfig: Record<string, { description: string }> = {
    'Paper 1': { description: 'Fiction & Imaginative Writing' },
    'Paper 2': { description: 'Non-Fiction & Transactional Writing' },
  };

  const pageTitle =
    step === 'subject'
      ? 'Practice Area'
      : step === 'paper'
        ? selectedSubject
        : `${selectedSubject} – ${selectedPaper}`;

  return (
    <>
      <PageHeader title={pageTitle} showBackButton onBackClick={handleBack} titleAlign="left">
        <p className="text-sm text-gray-600 text-left -mt-4">Practice each question type, one by one</p>
      </PageHeader>

      {/* Info Box — shown only on subject step */}
      {step === 'subject' && showInfoBox && (
        <div className="bg-blue-50 border-2 border-blue-200 rounded-[20px] mx-2 mt-6 mb-4 p-6 relative">
          <button
            onClick={() => setShowInfoBox(false)}
            className="absolute top-4 right-4 p-1 hover:bg-blue-200 rounded-full transition-colors"
            aria-label="Close info box"
          >
            <XMarkIcon className="w-5 h-5 text-gray-600" />
          </button>
          <div className="flex items-start gap-4 pr-8">
            <div className="flex-shrink-0 w-12 h-12 bg-blue-500 rounded-full flex items-center justify-center">
              <svg className="w-6 h-6 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z"
                />
              </svg>
            </div>
            <div className="flex-1">
              <h3 className="text-xl font-bold text-gray-900 mb-3">About Practice Area</h3>
              <p className="text-gray-700 mb-3">
                Use this space to practice specific question types and work on weaker topics.
              </p>
              <p className="text-gray-700">
                Before you start, make sure you have revised this subject using your other revision resources. You
                should already know the content quite well and understand the basics of exam technique. When you are
                consistently hitting your target grade or feel ready for a full paper, move to the Past Papers section
                to sit full exams under timed, exam-style conditions.
              </p>
            </div>
          </div>
        </div>
      )}

      {/* Step 1: Subject selection */}
      {step === 'subject' && (
        <div className="mx-2 mt-6">
          <h2 className="text-lg font-semibold mb-4">Choose a Subject</h2>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4 max-w-2xl">
            {['English Language', 'English Literature'].map((subject) => {
              const config = subjectConfig[subject];
              const isAvailable = availableSubjects.includes(subject) && subject !== 'English Literature';
              const questionCount = dbCategories.filter((c) => c.subject === subject).length;
              return (
                <button
                  key={subject}
                  onClick={() => handleSelectSubject(subject)}
                  disabled={!isAvailable}
                  className="group bg-white border-2 border-gray-200 rounded-xl p-6 text-left transition-all hover:border-primary hover:shadow-lg disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  <div className="flex items-start gap-4">
                    <div
                      className={`w-12 h-12 rounded-lg flex items-center justify-center flex-shrink-0 ${
                        subject === 'English Language' ? 'bg-primary/10' : 'bg-purple-100'
                      }`}
                    >
                      {config.icon}
                    </div>
                    <div className="flex-1">
                      <h3 className="font-semibold text-lg text-gray-900 group-hover:text-primary">{config.label}</h3>
                      <p className="text-sm text-gray-500 mt-1">{config.subtitle}</p>
                      <p className="text-xs text-gray-400 mt-2">
                        {isAvailable ? `${questionCount} question categories` : '0 question categories'}
                      </p>
                    </div>
                    <ChevronRightIcon className="w-5 h-5 text-gray-400 group-hover:text-primary" />
                  </div>
                </button>
              );
            })}
          </div>
        </div>
      )}

      {/* Step 2: Paper selection */}
      {step === 'paper' && (
        <div className="mx-2 mt-6">
          <h2 className="text-lg font-semibold mb-4">Choose a Paper</h2>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4 max-w-2xl">
            {(['Paper 1', 'Paper 2'] as PaperSelection[]).map((paper) => {
              const isAvailable = papersForSubject.includes(paper);
              return (
                <button
                  key={paper}
                  onClick={() => handleSelectPaper(paper)}
                  disabled={!isAvailable}
                  className="group bg-white border-2 border-gray-200 rounded-xl p-6 text-left transition-all hover:border-primary hover:shadow-lg disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  <div className="flex items-start gap-4">
                    <div className="w-12 h-12 bg-primary/10 rounded-lg flex items-center justify-center flex-shrink-0">
                      <DocumentTextIcon className="w-6 h-6 text-primary" />
                    </div>
                    <div className="flex-1">
                      <h3 className="font-semibold text-lg text-gray-900 group-hover:text-primary">{paper}</h3>
                      <p className="text-sm text-gray-500 mt-1">{paperConfig[paper]?.description}</p>
                    </div>
                    <ChevronRightIcon className="w-5 h-5 text-gray-400 group-hover:text-primary" />
                  </div>
                </button>
              );
            })}
          </div>
        </div>
      )}

      {/* Step 3: Question category + set size */}
      {step === 'question' && (
      <div className="flex flex-col gap-2 items-center flex-1 py-2">
        {/* Category Selection */}
        <div className="flex flex-col gap-3 w-full max-w-md">
          {categoryOptions.map((option) => (
            <div key={option.id}>
              <button
                onClick={() => handleCategorySelect(option.id)}
                className={`w-full px-5 py-4 rounded-2xl border-2 text-left transition-all ${
                  selectedId === option.id
                    ? 'bg-[#2B3440] border-[#2B3440] text-white'
                    : 'bg-white border-gray-300 hover:border-gray-400'
                }`}
              >
                <div className="flex items-center gap-3">
                  <div
                    className={`w-5 h-5 rounded-full border-2 flex-shrink-0 flex items-center justify-center ${
                      selectedId === option.id ? 'border-white' : 'border-gray-400'
                    }`}
                  >
                    {selectedId === option.id && <div className="w-2.5 h-2.5 rounded-full bg-primary" />}
                  </div>
                  <div className="flex-1">
                    <h3 className="font-semibold text-base">{formatLabel(option.label)}</h3>
                  </div>
                </div>
              </button>
              {/* Description below button */}
              {option.description && (
                <p className="text-sm text-gray-600 italic mt-1.5 text-center px-4">{option.description}</p>
              )}
            </div>
          ))}
        </div>

        {/* Set Size Selection */}
        {selectedId && (
          <div className="w-full max-w-md mt-4 font-bold">
            <label className="text-md">Choose set size {`[number of questions]`}</label>
            <div className={`relative ${isSectionB ? 'opacity-50 cursor-not-allowed' : ''}`}>
              <SelectInput
                value={setSize.toString()}
                onChange={(value) => setSetSize(parseInt(value))}
                options={[
                  { value: '1', label: '1' },
                  { value: '3', label: '3' },
                  { value: '5', label: '5' },
                ]}
                className="text-base font-normal bg-white"
                labelClassName="pr-10 [&>svg]:hidden"
                bordered
                readOnly={isSectionB}
                aria-readonly={isSectionB}
              />
              <ArrowDownIcon className="w-5 h-5 absolute right-4 top-1/2 -translate-y-1/2 pointer-events-none text-gray-600" />
            </div>
            {isSectionB && (
              <p className="text-xs text-gray-500 italic mt-1">Section B questions are limited to 1 question per set</p>
            )}
          </div>
        )}

        {/* Start Button */}
        {selectedId && (
          <LoadingButton
            loading={createPracticeSetMutation.isPending}
            onClick={handleStartSet}
            className="bg-primary hover:bg-brand-hover border-none text-white px-12 rounded-xl text-lg font-semibold w-full max-w-md mt-2"
          >
            Start set
          </LoadingButton>
        )}

        {/* Mock Past Paper Section */}
        <div className="w-full max-w-md mt-6 pt-6 border-t-2 border-gray-300">
          <div className="text-center mb-4">
            <h3 className="text-lg font-bold text-primary">Full Mock Exam</h3>
            <p className="text-sm italic mt-1 text-primary">
              Practice a complete mock {selectedPaper} paper to simulate a real past paper. This will not be timed and
              you will still have the 'help' button.
            </p>
          </div>
          <LoadingButton
            loading={createMixedPracticeSetMutation.isPending}
            onClick={handleMockPastPaper}
            className="bg-primary hover:bg-dark-hover-secondary border-none text-white px-12 rounded-xl text-lg font-semibold w-full"
          >
            Mock Past Paper
          </LoadingButton>
        </div>
      </div>
      )}

      {/* Usage Limit Error Modal */}
      {limitError && <DailyLimitModal errorMessage={limitError} onClose={() => setLimitError(null)} />}
    </>
  );
};

export default PracticeArea;
